/**
 * 鉴权过期统一处理（WIS-533）。
 *
 * 设计目标：掉登录后，无论请求来自 Umi `request`（含 `skipErrorHandler:true` 的执行服务、
 * 历史消息、用户资料/会员、视频复刻等）、原生 `fetch`（缩略图探测 / 素材下载），
 * 还是后端返回的 2xx `success:false` 鉴权体，都统一「清登录态 + 一次友好提示 + 跳登录页」。
 * 独立使用 new-api PAT 的 `/bff/token-platform/**` 不属于 Wischoicer 会话，由业务页本地重连，
 * 不再把 `Response status:401/403`、`Invalid JWT token`、`access token 无效` 等技术报错弹给用户。
 *
 * 详细设计见 docs/plans/2026-07-11-wis533-auth-expired-redirect-design.md。
 */
import { history } from '@umijs/max';
import { message } from 'antd';
import { clearAuthToken } from '@/utils/auth';

/** 鉴权过期时给用户看的统一文案。 */
export const AUTH_EXPIRED_MESSAGE = '登录状态已过期，请重新登录';

const LOGIN_PATH = '/user/login';
const PUBLIC_PATHS = new Set([
  LOGIN_PATH,
  '/user/forgot-password',
  '/user/register',
  '/user/register-result',
]);
/** 并发 401 防抖窗口：同一时间多个请求同时过期，只跳一次、只弹一次。 */
const REDIRECT_DEBOUNCE_MS = 2000;
const LOGOUT_URL = '/bff/gateway/user/logout';
const TOKEN_PLATFORM_PROXY_PREFIX = '/bff/token-platform/';

/**
 * 「会话过期」类短语（始终判定为鉴权过期）。
 * 故意收窄：不收裸 `expired` / `unauthorized` / `未授权` / 裸 `token` / `不一致`，
 * 避免把「链接已过期」「无权限」「两次密码不一致」等业务错误误判为掉登录。
 */
const SESSION_EXPIRED_PATTERNS = [
  '未登录',
  '登录已过期',
  '登录过期',
  '登录状态已过期',
  'access token 无效',
  'access token expired',
  'token 无效',
  'token已过期',
  'token expired',
  'token invalid',
  'invalid jwt',
  'invalid jwt token',
  'jwt token is expired',
  'jwt expired',
  'jwt token expired',
  'missing or invalid authorization header',
  'authorization header',
];

/**
 * 「身份不匹配」类短语（仅 HTTP 403 时才判定为鉴权过期）。
 * 这类错来自网关/BFF 的 user_id 校验（`Missing request header: user_id`、
 * `user_id ... does not match`、`请求 user_id 与当前登录用户不一致`），一律 HTTP 403。
 */
const IDENTITY_MISMATCH_PATTERNS = [
  'missing request header',
  '缺少请求头',
  'does not match',
  '不一致',
  '用户身份不匹配',
];

const matchesAny = (text: string, patterns: string[]): boolean => {
  if (!text) return false;
  const lower = String(text).toLowerCase();
  return patterns.some((p) => lower.includes(p));
};

/**
 * 从真实 transport 字段抽取 HTTP 状态码（axios `response.status` / Response-like `status`）。
 *
 * 故意 **不** 读 `BizError.info.errorCode` / `info.status`：那是 2xx `success:false` 的业务码，
 * 不是 HTTP 状态。若读它，带 `errorCode:401` 的业务错误（如「余额不足」）会被 `isAuthExpiredResponse`
 * 的「401 一律过期」快路径误判成登录过期 → 清 token、logout、跳登录，中断正常业务操作（WIS-533 P1）。
 */
const extractHttpStatus = (input: any): number => {
  if (!input) return 0;
  const raw =
    input.response?.status ??
    input.response?.statusCode ??
    input.status ??
    input.statusCode ??
    0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 从多种形态中抽取「人类可读的报错文案」。
 * 兼容 wischoicer 后端（`errorMessage`）、new-api（`message`）、网关英文 message、
 * FastAPI（`detail`）、以及 axios error 嵌套的 `response.data`。
 */
const extractMessage = (input: any): string => {
  if (input == null) return '';
  if (typeof input === 'string') return input;
  const data =
    input.response?.data ??
    input.data ??
    input.info ??
    input.error ??
    input.errorResponse ??
    input;
  if (data == null) return '';
  if (typeof data === 'string') return data;
  return String(
    data.errorMessage ??
      data.message ??
      data.error_description ??
      data.detail ??
      data.error ??
      '',
  );
};

/**
 * 判断一个响应/错误/数据体是否代表「鉴权过期」。
 *
 * - **BizError**（`errorThrower` 抛出的 2xx `success:false` 业务错误）：`info.errorCode` 是业务码
 *   不是 HTTP 状态码，**只**按已收窄的会话过期文案判定，绝不走「401 一律过期」快路径
 *   （否则 `errorCode:401` 的业务错误会误清登录态，WIS-533 P1）。
 * - 真实 transport `status === 401` → 始终 true（本系统 401 专用于未认证）。
 * - 真实 transport `status === 403` → 文案命中「会话过期」或「身份不匹配」才 true（避免误杀权限 403）。
 * - 其它/无 transport 状态码 → 仅文案命中「会话过期」才 true。
 *
 * 入参接受：axios error、`errorThrower` 抛出的 BizError、原生 `Response`-like、
 * 后端 data 对象、`success:false` 信封、或 `undefined`。
 */
export function isAuthExpiredResponse(input: any): boolean {
  if (!input) return false;
  const text = extractMessage(input);

  // BizError：业务错误码不是 HTTP 状态，只走会话过期文案判定（WIS-533 P1 防误杀）。
  if (input?.name === 'BizError') {
    return matchesAny(text, SESSION_EXPIRED_PATTERNS);
  }

  const status = extractHttpStatus(input);
  if (status === 401) return true;
  if (status === 403) {
    return (
      matchesAny(text, SESSION_EXPIRED_PATTERNS) ||
      matchesAny(text, IDENTITY_MISMATCH_PATTERNS)
    );
  }
  return matchesAny(text, SESSION_EXPIRED_PATTERNS);
}

/**
 * 从 axios/Umi 响应或错误对象中提取请求地址。
 * token-platform 透明代理使用独立的 new-api PAT 鉴权，不能与 Wischoicer JWT 混为一谈。
 */
const extractRequestUrl = (input: any): string =>
  String(
    input?.config?.url ??
      input?.response?.config?.url ??
      input?.request?.responseURL ??
      input?.responseURL ??
      '',
  );

const isTokenPlatformProxyRequest = (input: any): boolean => {
  const rawUrl = extractRequestUrl(input);
  if (!rawUrl) return false;
  try {
    return new URL(rawUrl, 'http://localhost').pathname.startsWith(
      TOKEN_PLATFORM_PROXY_PREFIX,
    );
  } catch (_e) {
    return rawUrl.split('?')[0].startsWith(TOKEN_PLATFORM_PROXY_PREFIX);
  }
};

/**
 * 仅判断 Wischoicer 主登录态是否过期。
 *
 * `/bff/token-platform/**` 的 401 属于 new-api PAT 鉴权域，由费用页面本地重连处理；
 * `/bff/gateway/user/token-platform/**` 仍属于 Wischoicer JWT 鉴权域，必须正常触发主站登出。
 */
export function shouldHandleWischoicerAuthExpired(input: any): boolean {
  return !isTokenPlatformProxyRequest(input) && isAuthExpiredResponse(input);
}

let lastHandledAt = 0;

/**
 * 鉴权过期统一跳转：清登录态 + 一次友好提示 + best-effort 清 BFF cookie + 跳登录页。
 *
 * - 防抖：`REDIRECT_DEBOUNCE_MS` 内重复调用直接 return（并发 401 只处理一次）。
 * - 已在登录页等公开路径直接 return，避免循环。
 * - `logout` 请求失败不阻塞跳转。
 * - 带 `redirect` 回原页（含 query/hash）。
 *
 * 在 `errorHandler` 顶层（`skipErrorHandler` guard 之前）调用即可覆盖所有走 Umi request
 * 的路径；原生 `fetch` 路径在各自调用点显式调用。
 */
export function handleAuthExpiredRedirect(_reason?: any): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now - lastHandledAt < REDIRECT_DEBOUNCE_MS) return;
  lastHandledAt = now;

  const { pathname, search = '', hash = '' } = window.location;
  if (PUBLIC_PATHS.has(pathname)) return;

  // 1. 清本地登录态（access token / token_type / user_id / aggregation_base_url + token-platform 凭证）
  clearAuthToken();

  // 2. 一次友好提示（防抖保证只弹一次）
  message.warning(AUTH_EXPIRED_MESSAGE);

  // 3. best-effort 清 BFF cookie，失败不阻塞
  try {
    void fetch(LOGOUT_URL, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      /* ignore: 清 cookie 失败不阻塞跳转 */
    });
  } catch (_e) {
    /* ignore */
  }

  // 4. 跳登录页，带 redirect 回原页
  const redirect = `${pathname}${search || ''}${hash || ''}`;
  const target = `${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`;
  try {
    history.push(target);
  } catch (_e) {
    // history 不可用时降级整页跳转
    window.location.href = target;
  }
}

/**
 * 把任意请求错误转成「用户可理解」的中文文案，绝不回退成 `Response status:xxx`。
 * 鉴权类错误应由调用方先用 `isAuthExpiredResponse` 拦截跳转；这里只兜底非鉴权错误。
 */
export function resolveFriendlyErrorMessage(error: any): string {
  if (error?.name === 'BizError') {
    const bizMsg = error?.info?.errorMessage;
    if (bizMsg) return String(bizMsg);
  }
  const backendMessage =
    error?.response?.data?.message ??
    error?.response?.data?.errorMessage ??
    error?.info?.errorMessage ??
    error?.data?.message ??
    '';
  if (backendMessage) return String(backendMessage);

  if (error?.response) {
    // 有 HTTP 响应但无后端文案：按状态码给可理解兜底
    const status = Number(error.response.status) || 0;
    if (status >= 500) return '服务暂时不可用，请稍后重试';
    return '请求失败，请稍后重试';
  }
  if (error?.request) {
    // 请求已发出但无响应：网络问题
    return '网络异常，请检查网络后重试';
  }
  return '请求失败，请稍后重试';
}

/** 仅供单测重置防抖状态，业务代码不要调用。 */
export function __resetAuthExpiredDebounceForTest(): void {
  lastHandledAt = 0;
}
